import React from "react";
import { Users, Building2, ScrollText, Ban, Plug, SlidersHorizontal, Sparkles, Map, Radar, Cpu } from "lucide-react";
import { TeamMembersTab } from "@/components/settings/TeamMembersTab";
import { BrandIntelligenceTab } from "@/components/settings/BrandIntelligenceTab";
import { BrandManagementTab } from "@/components/settings/BrandManagementTab";
import { LandscapeConfigurationTab } from "@/components/settings/LandscapeConfigurationTab";
import { LandscapeLogTab } from "@/components/settings/LandscapeLogTab";
import { ApiIntegrationsTab } from "@/components/settings/ApiIntegrationsTab";
import { KPSConfigurationTab } from "@/components/settings/KPSConfigurationTab";
import { AIConfigurationTab } from "@/components/settings/AIConfigurationTab";
import { GlobalProhibitedKeywordsTab } from "@/components/settings/GlobalProhibitedKeywordsTab";
import { OrganizationsTab } from "@/components/settings/OrganizationsTab";
import { AIUsageTab } from "@/components/settings/AIUsageTab";
import type { AccountResponse } from "@/types";

export type SettingsSection = {
  id: string;
  label: string;
  description: string;
  icon: typeof Users;
  disabled?: boolean;
  // Hidden from the index and unresolvable by `?tab=` when false. UX only -- not authorization.
  visibleTo?: (account: AccountResponse) => boolean;
  render: () => React.JSX.Element;
};

export type SettingsGroup = {
  id: string;
  label: string;
  description: string;
  sections: SettingsSection[];
};

export const settingsGroups: SettingsGroup[] = [
  {
    id: "people-brands",
    label: "People & Brands",
    description: "Manage who has access and the brands they work on.",
    sections: [
      {
        id: "team",
        label: "Team Members",
        description: "Invite teammates and manage roles and access.",
        icon: Users,
        render: () => <TeamMembersTab />,
      },
      {
        id: "brands",
        label: "Brand Management",
        description: "Add, edit, and organize the brands in your workspace.",
        icon: Building2,
        render: () => <BrandManagementTab />,
      },
      {
        id: "organizations",
        label: "Organizations",
        description: "View and manage all organizations in the platform.",
        icon: Building2,
        render: () => <OrganizationsTab />,
      },
    ],
  },
  {
    id: "logs",
    label: "Logs",
    description: "Monitoring run history and AI usage — spend, tokens, and a call-by-call audit trail.",
    sections: [
      {
        id: "landscape-logs",
        label: "Landscape Log",
        description: "Review the history and status of landscape runs.",
        icon: ScrollText,
        render: () => <LandscapeLogTab />,
      },
      {
        id: "ai-usage",
        label: "AI Usage",
        description: "Track AI spend, tokens, and every individual AI call.",
        icon: Cpu,
        visibleTo: (a) => a.is_platform_admin === true,
        render: () => <AIUsageTab />,
      },
    ],
  },
  {
    id: "configurations",
    label: "Configurations",
    description: "Tune integrations, scoring, AI behavior, and content guardrails.",
    sections: [
      {
        id: "prohibited-keywords",
        label: "Prohibited Keywords",
        description: "Global keyword blocklist applied to every AI-generated output.",
        icon: Ban,
        render: () => <GlobalProhibitedKeywordsTab />,
      },
      {
        id: "api",
        label: "API & Integrations",
        description: "Manage API keys, webhooks, and third-party connections.",
        icon: Plug,
        render: () => <ApiIntegrationsTab />,
      },
      {
        id: "kps",
        label: "KPS Configuration",
        description: "Adjust how Keyword Priority Score weights are calculated.",
        icon: SlidersHorizontal,
        render: () => <KPSConfigurationTab />,
      },
      {
        id: "brand-intelligence",
        label: "Brand Intelligence",
        description: "Tune classification bands, growth/trend thresholds, category-health weights, and refresh cooldown.",
        icon: Radar,
        render: () => <BrandIntelligenceTab />,
      },
      {
        id: "landscape-configuration",
        label: "Landscape Configuration",
        description: "Set the minimum search volume and ranked-competitor thresholds for landscape generation.",
        icon: Map,
        render: () => <LandscapeConfigurationTab />,
      },
      {
        id: "ai",
        label: "AI Configuration",
        description: "Set defaults for AI-generated content and recommendations.",
        icon: Sparkles,
        render: () => <AIConfigurationTab />,
      },
    ],
  },
];

export const allSections: SettingsSection[] = settingsGroups.flatMap((g) => g.sections);
